<script setup lang="ts">
import debounce from 'lodash/debounce';
import { ApexOptions } from 'apexcharts';
import { ref, onMounted, onUnmounted, watch, computed, toRaw, nextTick } from 'vue';
import { DashboardWidget, Serie, ApexChartType } from '@/interfaces/Dashboard';
import { buildColorRanges, normalizeChartType } from '@/lib/chart-helpers';
import ConnxSkeletonLineGraph from './ConnxSkeletonLineGraph.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import ConnxSkeletonDonutGraph from './ConnxSkeletonDonutGraph.vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';

type PlotOptions = ApexOptions['plotOptions'];

const props = defineProps<{ height: number; width: number; widget: DashboardWidget; options?: ApexOptions }>();
const emits = defineEmits(['data-updated']);
const dashboardStore = useDashboardStore();

// --- state
const data = ref<any>(null);
const status = ref<'idle' | 'loading' | 'formatting' | 'ready' | 'error'>('idle');
const chart = ref();
const renderIncrementer = ref(1);
const isComponentMounted = ref(false);
const chartInstance = ref<any>(null);
const isDestroying = ref(false); // Add destruction flag

const seriesData = ref<{ labels: string[] | null; series: any[] }>({ labels: null, series: [] });
const plotOptions = ref<PlotOptions>({});
const type = ref<ApexChartType>();
const rawType = ref<string>();

// --- split chartOptions
const baseOptions = ref<ApexOptions>({ ...toRaw(props.options) });
const patchOptions = ref<ApexOptions>({});
const chartOptions = computed<ApexOptions>(() => ({
  ...baseOptions.value,
  ...patchOptions.value,
}));

// --- lifecycle
onMounted(() => {
  isComponentMounted.value = true;
  fetchData();
});

onUnmounted(() => {
  isComponentMounted.value = false;
  safeDestroy();
});

const safeDestroy = () => {
  if (isDestroying.value) return;

  isDestroying.value = true;
  if (chartInstance.value && typeof chartInstance.value.destroy === 'function') {
    try {
      chartInstance.value.destroy();
    } catch (error) {
      console.warn('Error destroying chart instance:', error);
    }
    chartInstance.value = null;
  }
};

// Store chart instance when it's created
const onChartRendered = (chartContext: any, config: any) => {
  if (isDestroying.value) return;
  chartInstance.value = chartContext;
};

// --- derived state
const hasData = computed(() => seriesData.value.series.length > 0);

const shouldRenderChart = computed(
  () =>
    isComponentMounted.value &&
    status.value === 'ready' &&
    hasData.value &&
    !!chartOptions.value.chart?.type &&
    !isDestroying.value
);

const renderKey = computed(() => `${props.widget.dashboardTabWidgetGuid}-${rawType.value}-${renderIncrementer.value}`);

const labelStyles = {
  fontSize: '13px',
  fontWeight: 'bold',
  colors: 'var(--connx-grey-shade-20)',
};

const percentageFormatter = (val: any) => {
  if (typeof val !== 'number' || isNaN(val)) return val;
  if (val === 0) return '';
  const fixed = val.toFixed(1);
  return `${String(fixed.endsWith('.0') ? parseInt(fixed) : fixed)}%`;
};

const currencyFormatter = (val: number) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(val);

const countFormatter = (val: any) => (val === 0 ? '' : val);

const chartHeight = computed(() => {
  const STANDARD_PADDING = 18;
  if (props.widget.defaultJsonProperties.currentDisplayType?.toLowerCase() === 'bar' && data.value) {
    const seriesInput = getSeriesInput();
    const barHeight = 40;
    const calculatedHeight = seriesInput.length * barHeight;
    return calculatedHeight < props.height ? props.height - STANDARD_PADDING : calculatedHeight;
  }
  if (props.widget.defaultJsonProperties.currentDisplayType?.toLowerCase() === 'column' && data.value) {
    if (typeof chartWidth.value === 'number' && chartWidth.value > props.width) {
      return props.height - STANDARD_PADDING;
    }
  }
  return '100%';
});

const chartWidth = computed(() => {
  if (props.widget.defaultJsonProperties.currentDisplayType?.toLowerCase() === 'column' && data.value) {
    const barWidth = 40;
    const rawWidth = getSeriesInput().length * barWidth;
    if (rawWidth > props.width) {
      return rawWidth;
    }
  }
  return '100%';
});

const skeletonMap = new Map([
  ['default', ConnxSkeletonLineGraph],
  ['line', ConnxSkeletonLineGraph],
  ['donut', ConnxSkeletonDonutGraph],
]);

const reRender = debounce(() => {
  if (!isComponentMounted.value || isDestroying.value) return;
  renderIncrementer.value += 1;
}, 50);

const getSeriesInput = () => {
  const raw = data.value?.data;
  const series: Serie[] = Array.isArray(raw) ? raw : raw?.series ?? [];
  return series;
};

// --- forceOptionsUpdate (only affects patchOptions)
const forceOptionsUpdate = (opts: ApexOptions) => {
  if (!isComponentMounted.value || isDestroying.value) return;
  patchOptions.value = {
    ...toRaw(patchOptions.value),
    ...opts,
  };
  reRender();
};

// --- formatData (only affects baseOptions)
const formatData = async () => {
  if (!isComponentMounted.value || isDestroying.value) return;

  status.value = 'formatting';
  try {
    const seriesInput = getSeriesInput();
    seriesData.value = { labels: null, series: [] };

    if (!seriesInput || seriesInput.length === 0) {
      status.value = 'ready';
      return;
    }

    const properties = props.widget.defaultJsonProperties;
    rawType.value = properties.currentDisplayType?.toLowerCase() ?? 'line';
    const chartType = normalizeChartType(rawType.value);
    type.value = chartType;

    const circularChart = ['donut', 'pie', 'radialBar'].includes(chartType);

    const isObjectArray = Array.isArray(seriesInput) && typeof seriesInput[0] === 'object';
    const ranges = properties.parameters?.benchmarks ? buildColorRanges(properties.parameters.benchmarks) : null;

    const labels = isObjectArray ? seriesInput.map((item: any) => item.name ?? 'Unknown') : null;

    // --- build series
    if (circularChart) {
      if (isObjectArray) {
        seriesData.value = {
          labels,
          series: seriesInput.map((item: any) => item.data ?? 0),
        };
      } else {
        seriesData.value = { labels, series: seriesInput };
      }
    } else {
      if (isObjectArray) {
        seriesData.value = {
          labels,
          series: [{ name: '', data: seriesInput.map((item: any) => item.data ?? 0) }],
        };
      } else {
        seriesData.value = { labels, series: [{ name: '', data: seriesInput }] };
      }
    }
    // --- formatters
    const unit = properties.currentDisplayUnit;
    const valueFormatter =
      unit === 'percent' || circularChart
        ? percentageFormatter
        : unit === 'currency'
        ? currencyFormatter
        : countFormatter;

    const values = Array.isArray(seriesInput)
      ? seriesInput.map((item) => (typeof item === 'object' ? item.data ?? 0 : item))
      : [];

    const totalCount = Number((values as number[]).reduce((sum, val) => sum + val, 0));
    const hasTotal = !isNaN(totalCount) && isFinite(totalCount);

    // --- plotOptions
    if (chartType === 'donut') {
      plotOptions.value = {
        pie: {
          donut: {
            size: '40%',
            labels: {
              show: true,
              value: { formatter: valueFormatter },
              total: hasTotal
                ? {
                    show: true,
                    label: 'Total',
                    formatter: () => {
                      const fixed = totalCount.toFixed(1);
                      return String(fixed.endsWith('.0') ? parseInt(fixed) : fixed);
                    },
                  }
                : undefined,
            },
          },
        },
      };
    } else if (chartType === 'radialBar') {
      plotOptions.value = {
        radialBar: {
          hollow: { size: '40%' },
          dataLabels: {
            name: { color: '#FF0000' },
            value: { color: '#0000FF', formatter: valueFormatter },
            total: hasTotal
              ? {
                  show: true,
                  label: 'Total',
                  formatter: () => {
                    const fixed = totalCount.toFixed(1);
                    return String(fixed.endsWith('.0') ? parseInt(fixed) : fixed);
                  },
                }
              : undefined,
          },
        },
      };
    } else if (chartType === 'bar' || rawType.value === 'column') {
      plotOptions.value = {
        bar: {
          horizontal: rawType.value !== 'column',
          distributed: true,
          borderRadius: 10,
          borderRadiusApplication: 'end',
          columnWidth: '100%',
          barHeight: rawType.value === 'column' ? '100%' : '95%',
          borderWidth: 0,
          dataLabels: { position: 'center' },
          ...(ranges && { colors: { ranges } }),
        },
      } as PlotOptions;
    } else if (chartType === 'heatmap' && ranges) {
      plotOptions.value = {
        heatmap: {
          distributed: true,
          colorScale: { ranges },
        },
      };
    } else {
      plotOptions.value = {};
    }

    const isSeriesChart = ['line', 'area', 'bar', 'scatter', 'radar', 'heatmap', 'rangeArea', 'rangeBar'].includes(
      chartType
    );

    const xaxis = isSeriesChart
      ? { ...properties.chartOptions?.xaxis, categories: labels }
      : { ...properties.chartOptions?.xaxis };

    // Special handling for column charts to prevent width calculation issues
    const finalChartWidth = rawType.value === 'column' ? chartWidth.value : '100%';
    const finalChartHeight = rawType.value === 'column' ? chartHeight.value : '100%';

    // --- baseOptions
    baseOptions.value = {
      grid: { padding: { top: 0, right: 15, bottom: 0, left: 15 } },
      ...properties.chartOptions,
      ...(circularChart ? { labels } : {}),
      tooltip: {
        enabled: true,
        followCursor: true,
        custom: function ({ series, seriesIndex, w, dataPointIndex }) {
          let label = seriesData.value.labels ? seriesData.value.labels[dataPointIndex ?? seriesIndex] : '';
          let value;
          if (Array.isArray(series[seriesIndex])) {
            value = series[seriesIndex][dataPointIndex ?? 0];
          } else {
            value = series[seriesIndex];
          }
          switch (unit) {
            case 'percent':
              const fixed = value.toFixed(1);
              value = `${String(fixed.endsWith('.0') ? parseInt(fixed) : fixed)}%`;
              break;
            case 'currency':
              value = `$${value}`;
              break;
          }

          return `
            <div class="tw-text-sm tw-border-none tw-p-1 tw-rounded-sm tw-text-connx-button-text tw-bg-connx-primary">
              <strong class="tw-text-connx-button-text">${label}</strong>: <span>${value}</span>
            </div>
          `;
        },
      },
      yaxis: {
        labels: {
          offsetY: 0,
          style: labelStyles,
          formatter: valueFormatter,
        },
      },
      dataLabels: {
        enabled: true,
        offsetX: 0,
        dropShadow: { enabled: false },
        style: {
          colors: ['var(--connx-white)'],
        },
        background: {
          enabled: true,
          backgroundColor: 'var(--connx-primary-shade-25)',
          foreColor: 'var(--connx-white)',
          borderRadius: 5,
          padding: 4,
          opacity: 0.5,
          borderWidth: 1,
          borderColor: 'var(--connx-primary-tint-25)',
        },
        formatter: valueFormatter,
      },
      chart: {
        type: chartType,
        width: finalChartWidth,
        height: finalChartHeight,
        // Add animations configuration for better stability
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      xaxis: {
        labels: {
          style: labelStyles,
          formatter: valueFormatter,
        },
        ...xaxis,
      },
      plotOptions: { ...plotOptions.value, ...properties.chartOptions?.plotOptions },
      legend: {
        position: circularChart ? 'bottom' : 'top',
        show: circularChart ? seriesData.value.series.length < 7 : false,
        formatter: (seriesName: string) => (seriesName.toLowerCase().includes('series') ? '' : seriesName),
        ...properties.chartOptions?.legend,
      },
      ...props.options,
    } as ApexOptions;

    // Wait for next tick to ensure DOM is ready
    await nextTick();

    // Special delay for column charts to ensure DOM is fully settled
    if (rawType.value === 'column') {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    reRender();
    status.value = 'ready';
  } catch (err) {
    console.error('formatData error:', err);
    status.value = 'error';
  }
};

// --- fetching
const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

const fetchData = async () => {
  if (!isComponentMounted.value || isDestroying.value) return;

  status.value = 'loading';
  try {
    data.value = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });
    emits('data-updated', data.value);
    await formatData();
  } catch (err) {
    console.error('fetchData error:', err);
    status.value = 'error';
  }
};

// Add error handling for chart updates
const safeUpdateOptions = (newOptions: ApexOptions, redraw = false, animate = true, updateSyncedCharts = true) => {
  if (chartInstance.value && typeof chartInstance.value.updateOptions === 'function' && !isDestroying.value) {
    try {
      chartInstance.value.updateOptions(newOptions, redraw, animate, updateSyncedCharts);
    } catch (error) {
      console.warn('Error updating chart options:', error);
    }
  }
};

// Fix the watchers with better column chart handling
watch(
  () => {
    const p = props.widget?.defaultJsonProperties?.parameters;
    return {
      results: p?.results ?? null,
      unit: p?.unit ?? null,
      benchmarkValues: p?.benchmarks?.values,
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
      groupByValue: p?.groupBy?.value ?? null,
      orderByValue: p?.orderBy?.value ?? null,
      previousMonthsValue: p?.previousMonths?.value,
      previousRangeSelected: p?.previousRange?.selected ?? false,
      previousRangeFrom: p?.previousRange?.value[0] ?? '',
      previousRangeTo: p?.previousRange?.value[1] ?? '',
      // Add chart type to the dependency for column charts
      chartType: props.widget?.defaultJsonProperties?.currentDisplayType,
    };
  },
  async (newVal, oldVal) => {
    if (!isComponentMounted.value || !oldVal || isDestroying.value) return;

    if (newVal.previousMonthsValue) {
      const min = props.widget?.defaultJsonProperties?.parameters?.previousMonths?.min ?? 1;
      const max = props.widget?.defaultJsonProperties?.parameters?.previousMonths?.max ?? null;

      if (newVal.previousMonthsValue < min) return;
      if (max && newVal.previousMonthsValue > max) return;
    }

    // Only fetch if values really changed
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      // Special handling for column chart type changes
      if (newVal.chartType === 'column' && oldVal.chartType === 'column') {
        // Add a small delay for column charts to ensure stability
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      await debouncedFetchData();
    }
  },
  { deep: false, flush: 'post' }
);

// Fix the weights watcher
watch(
  () => props.widget?.defaultJsonProperties?.parameters?.weights?.values,
  async (newVal) => {
    if (!isComponentMounted.value || isDestroying.value) return;

    const sum = newVal?.reduce((total, val) => total + (Number(val) || 0), 0);
    if (sum !== 100) return;

    // Special delay for column charts
    if (props.widget?.defaultJsonProperties?.currentDisplayType === 'column') {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    await debouncedFetchData();
  },
  { deep: true, flush: 'post' }
);

// Add a specific watcher for column chart dimensions
watch(
  () => [props.height, props.width],
  async () => {
    if (!isComponentMounted.value || isDestroying.value) return;

    // Only handle dimension changes for column charts
    if (props.widget?.defaultJsonProperties?.currentDisplayType === 'column') {
      await new Promise((resolve) => setTimeout(resolve, 150));
      reRender();
    }
  },
  { flush: 'post' }
);

defineExpose({
  reRender,
  formatData,
  getSeriesInput,
  forceOptionsUpdate,
  safeDestroy,
  safeUpdateOptions,
});
</script>

<template>
  <div
    class="tw-w-full"
    :class="{
      'ScrollBar ScrollBar--vertical ScrollBar--horizontal-hidden': rawType === 'bar',
      'ScrollBar ScrollBar--horizontal ScrollBar--vertical-hidden': rawType === 'column',
    }"
  >
    <div :style="{ height: `${height}px` }">
      <vue-apex-charts
        v-if="shouldRenderChart"
        ref="chart"
        :type="type"
        :key="renderKey"
        :height="chartHeight"
        :width="chartWidth"
        :options="chartOptions"
        :series="seriesData.series"
        @rendered="onChartRendered"
      />
      <connx-dashboard-widget-no-data v-else-if="status === 'ready' && !hasData" />
      <component v-else-if="status === 'loading'" :is="skeletonMap.get(type!) ?? skeletonMap.get('default')" />
    </div>
  </div>
</template>
